import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import {
    ArrowLeftRight,
    ArrowRightLeft,
    ArrowUpDown,
    BarChart3,
    LayoutGrid,
    ScrollText,
    Settings,
    Target,
    Warehouse,
} from 'lucide-react';

import styled from 'styled-components';

import { ExternalColumnType, GetMicrostoreColumnQuery, useUpdateMicrostoreColumnMutation } from 'data/__generated__';

import {
    ActionsAndSave,
    EntityPageBreadcrumb,
    PageActions,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { QuantityGenerationPopup } from 'pages/MicrostoreColumnGroups/Popups/QuantityGenerationPopup';
import { StockTransferGenerationPopup } from 'pages/MicrostoreColumnGroups/Popups/StockTransferGenerationPopup';
import { MoveColumnPopup } from 'pages/MicrostoreColumnGroups/Popups/MoveColumnPopup';
import { ReplaceMicrostoreColumnPopup } from 'pages/MicrostoreColumnGroups/Popups/ReplaceMicrostoreColumnPopup';
import { ReplaceMicrostoreColumnTemplatePopup } from 'pages/MicrostoreColumnGroups/Popups/ReplaceMicrostoreColumnTemplatePopup';
import { GeneralInfoSection } from './GeneralInfoSection';
import { StatesSection } from './StatesSection';
import { IntegrationsSection } from './IntegrationsSection';
import { NayaxMDBSection } from './NayaxMDBSection';
import { EmplacementsSection } from './EmplacementsSection';
import { ProductStocksSection } from './ProductStocksSection';
import { TrayLocationsSection } from './TrayLocationsSection';
import {
    mapMicrostoreColumnFormValuesToMicrostoreColumnUpdateInput,
    MicrostoreColumnFormValues,
} from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/FormHelper/DataMapper';
import { AuditLogs } from 'components/AuditLogs';
import { SectionContainer } from 'components/DetailsView/Section/SectionContainer';

export const MicrostoreColumnFormContent = ({
    microstoreColumn,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
}) => {
    const methods = useForm<MicrostoreColumnFormValues>({ defaultValues: microstoreColumn });
    const {
        formState: { isDirty },
        handleSubmit,
        reset,
        watch,
    } = methods;

    const [updateMicrostoreColumn, { loading: updateLoading }] = useUpdateMicrostoreColumnMutation();

    const handleFormSubmit = handleSubmit(async (fields) => {
        if (!microstoreColumn) {
            return;
        }

        const microstoreColumnUpdateInput = mapMicrostoreColumnFormValuesToMicrostoreColumnUpdateInput({
            microstoreColumnFormValues: fields,
        });
        const { data } = await updateMicrostoreColumn({
            variables: { microstoreColumnUpdateInput },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la modification du template de colonne');
        }

        const {
            updateMicrostoreColumnMutation: { newMicrostoreColumn, errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>);
            });
        }

        if (newMicrostoreColumn) {
            toast.success('La colonne a bien été modifiée !');
            reset(fields);
            //setIsFormDirty(false);
            //setRevertUpdateStack([]);
        }
    });

    const { columnGroup } = microstoreColumn;
    const site = columnGroup.site;
    const totem = site.totem;
    const organization = totem.organization;

    const [isQuantityPopupOpen, setIsQuantityPopupOpen] = useState(false);
    const [isTransferPopupOpen, setIsTransferPopupOpen] = useState(false);
    const [isMovePopupOpen, setIsMovePopupOpen] = useState(false);
    const [isReplacePopupOpen, setIsReplacePopupOpen] = useState(false);
    const [isReplaceTemplatePopupOpen, setIsReplaceTemplatePopupOpen] = useState(false);

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Colonne',
            items: [
                {
                    label: 'Changer de place la colonne',
                    icon: <ArrowUpDown />,
                    onClick: () => setIsMovePopupOpen(true),
                },
                {
                    label: 'Remplacer la colonne',
                    icon: <ArrowRightLeft />,
                    onClick: () => setIsReplacePopupOpen(true),
                },
                {
                    label: 'Changer le template de la colonne',
                    icon: <Settings />,
                    onClick: () => setIsReplaceTemplatePopupOpen(true),
                },
                {
                    label: 'Générer quantités',
                    icon: <Target />,
                    onClick: () => setIsQuantityPopupOpen(true),
                },
                {
                    label: 'Transferts de stock',
                    icon: <ArrowLeftRight />,
                    onClick: () => setIsTransferPopupOpen(true),
                },
            ],
        },
        {
            label: 'Navigation',
            items: [
                { label: 'Stocks de la colonne', icon: <BarChart3 />, to: `/column/${microstoreColumn._id}/stocks` },
                { label: 'Ouvrir le groupe', icon: <ScrollText />, to: `/microstore-column-group/${columnGroup._id}` },
                { label: 'Ouvrir le site', icon: <Warehouse />, to: `/site/${site._id}` },
                { label: 'Planogramme visuel', icon: <LayoutGrid />, to: `/planogram/${site._id}` },
            ],
        },
    ];

    return (
        <Container>
            <Helmet title={`${microstoreColumn.name} · TOTADMIN`} />
            <FormProvider {...methods}>
                <Form onSubmit={handleFormSubmit}>
                    <PageHeader>
                        <EntityPageBreadcrumb
                            organization={organization}
                            totem={totem}
                            site={site}
                            columnGroup={columnGroup}
                            column={microstoreColumn}
                        />
                        <PageActions>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={handleFormSubmit}
                                saveDisabled={!isDirty}
                                saveLoading={updateLoading}
                                saveDataTest="microstore-column-save"
                            />
                        </PageActions>
                    </PageHeader>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection microstoreColumn={microstoreColumn} />
                            <IntegrationsSection microstoreColumn={microstoreColumn} isDirty={isDirty} />
                            {watch('externalColumnType') === ExternalColumnType.NayaxNeocorner && (
                                <NayaxMDBSection microstoreColumn={microstoreColumn} />
                            )}
                            <EmplacementsSection microstoreColumn={microstoreColumn} />
                            <ProductStocksSection microstoreColumn={microstoreColumn} />
                            <TrayLocationsSection microstoreColumn={microstoreColumn} />
                            <StatesSection microstoreColumn={microstoreColumn} isDirty={isDirty} />
                            <SectionContainer title="Audit logs" isInitiallyOpen={false}>
                                <AuditLogs collectionDocumentId={microstoreColumn._id} />
                            </SectionContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
            {isQuantityPopupOpen ? (
                <QuantityGenerationPopup
                    isOpen={isQuantityPopupOpen}
                    setIsOpen={setIsQuantityPopupOpen}
                    initialColumns={[
                        {
                            _id: microstoreColumn._id,
                            name: microstoreColumn.name,
                            siteName: microstoreColumn.siteName,
                        },
                    ]}
                    isLocked
                />
            ) : null}
            {isTransferPopupOpen ? (
                <StockTransferGenerationPopup
                    isOpen={isTransferPopupOpen}
                    setIsOpen={setIsTransferPopupOpen}
                    initialColumns={[
                        {
                            _id: microstoreColumn._id,
                            name: microstoreColumn.name,
                            siteName: microstoreColumn.siteName,
                        },
                    ]}
                    isLocked
                />
            ) : null}
            {isMovePopupOpen ? (
                <MoveColumnPopup
                    isOpen={isMovePopupOpen}
                    setMicrostoreColumnToMove={() => setIsMovePopupOpen(false)}
                    column={microstoreColumn}
                />
            ) : null}
            {isReplacePopupOpen ? (
                <ReplaceMicrostoreColumnPopup
                    isOpen={isReplacePopupOpen}
                    setMicrostoreColumnToReplace={() => setIsReplacePopupOpen(false)}
                    column={microstoreColumn}
                />
            ) : null}
            {isReplaceTemplatePopupOpen ? (
                <ReplaceMicrostoreColumnTemplatePopup
                    isOpen={isReplaceTemplatePopupOpen}
                    setMicrostoreColumnToReplaceTemplate={() => setIsReplaceTemplatePopupOpen(false)}
                    column={microstoreColumn}
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
